import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  Share2,
  Copy,
  Check,
  X,
  QrCode,
  Code,
  MessageCircle,
  ExternalLink,
  ShieldCheck,
  Building2,
  UserCheck,
  Users,
  Sparkles,
  CheckCircle2,
} from 'lucide-react';
import { QrCodeViewer } from '../../../shared/components/QrCodeViewer.js';
import { LazyAvatar } from '../../../shared/components/LazyAvatar.js';
import { resolveCoverUrl } from '../../../shared/utils/imageCompressor.js';

interface ShareShowcaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  shareUrl: string;
  qrUrl?: string;
  type: 'penulis' | 'penerbit' | 'organisasi';
  publisherCode?: string | null;
  totalBooks: number;
  avatarUrl?: string;
}

export const ShareShowcaseModal: React.FC<ShareShowcaseModalProps> = ({
  isOpen,
  onClose,
  title,
  shareUrl,
  qrUrl,
  type,
  publisherCode,
  totalBooks,
  avatarUrl,
}) => {
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedWidget, setCopiedWidget] = useState(false);
  const [activeTab, setActiveTab] = useState<'link' | 'qr' | 'widget'>('link');
  const [widgetTheme, setWidgetTheme] = useState<'light' | 'dark' | 'indigo'>('light');

  if (!isOpen) return null;

  const typeLabel = type === 'organisasi' ? 'Organisasi & Komunitas' : type === 'penerbit' ? 'Penerbit' : 'Penulis';
  
  // Tampilan tautan bersih tanpa encoding persen (%20) untuk representasi visual
  const displayUrl = decodeURIComponent(shareUrl);

  const whatsappText = encodeURIComponent(
    `📚 Katalog Resmi UIBN ${typeLabel} "${title}":\n` +
      `Lihat ${totalBooks} buku terverifikasi dengan kode unik UIBN digital:\n${displayUrl}`
  );
  const whatsappUrl = `https://api.whatsapp.com/send?text=${whatsappText}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(displayUrl);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2500);
    } catch {
      // Fallback
    }
  };

  const getWidgetCode = () => {
    const bgColor = widgetTheme === 'dark' ? '#1e293b' : widgetTheme === 'indigo' ? '#4f46e5' : '#ffffff';
    const textColor = widgetTheme === 'light' ? '#1e293b' : '#ffffff';
    const borderColor = widgetTheme === 'light' ? '#e2e8f0' : widgetTheme === 'indigo' ? '#6366f1' : '#334155';
    const btnBg = widgetTheme === 'indigo' ? '#ffffff' : '#4f46e5';
    const btnText = widgetTheme === 'indigo' ? '#4f46e5' : '#ffffff';
    const avatarBorder = widgetTheme === 'indigo' ? '#ffffff40' : '#4f46e540';
    
    const resolvedAvatar = avatarUrl ? resolveCoverUrl(avatarUrl) : '';
    const widgetAvatarSrc = resolvedAvatar
      ? (resolvedAvatar.startsWith('http') ? resolvedAvatar : `${typeof window !== 'undefined' ? window.location.origin : 'https://uibn.id'}${resolvedAvatar.startsWith('/') ? '' : '/'}${resolvedAvatar}`)
      : 'https://cdn.uibn.web.id/default-avatar.png';
    
    return `<!-- UIBN Profile Widget: ${title} -->
<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; max-width: 350px; border: 1px solid ${borderColor}; border-radius: 16px; padding: 20px; background: ${bgColor}; color: ${textColor}; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);">
  <div style="display: flex; align-items: center; gap: 14px; margin-bottom: 16px;">
    <img src="${widgetAvatarSrc}" alt="${title}" style="width: 52px; height: 52px; border-radius: 12px; object-fit: cover; border: 2px solid ${avatarBorder};" />
    <div style="min-width: 0;">
      <div style="font-weight: 800; font-size: 15px; margin-bottom: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${title}</div>
      <div style="font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; opacity: 0.8;">${typeLabel} TERVERIFIKASI</div>
    </div>
  </div>
  <div style="font-size: 13px; line-height: 1.5; margin-bottom: 18px; opacity: 0.9;">
    Katalog resmi berisikan <strong>${totalBooks} naskah buku</strong> ber-UIBN digital yang telah terverifikasi.
  </div>
  <a href="${displayUrl}" target="_blank" rel="noopener noreferrer" style="display: block; text-align: center; background: ${btnBg}; color: ${btnText}; padding: 12px; border-radius: 12px; text-decoration: none; font-weight: 700; font-size: 13px; transition: opacity 0.2s;">
    Lihat Katalog Publik
  </a>
</div>`;
  };

  const handleCopyWidget = async () => {
    try {
      await navigator.clipboard.writeText(getWidgetCode());
      setCopiedWidget(true);
      setTimeout(() => setCopiedWidget(false), 2500);
    } catch {
      // Fallback
    }
  };

  const tabs = [
    { id: 'link', label: 'Tautan Publik', icon: Share2 },
    { id: 'qr', label: 'QR Code', icon: QrCode },
    { id: 'widget', label: 'Widget Website', icon: Code },
  ];

  return createPortal(
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          transition={{ type: 'spring', stiffness: 350, damping: 25 }}
          className="bg-white rounded-3xl max-w-lg w-full shadow-2xl border border-slate-100 overflow-hidden my-auto flex flex-col"
        >
          {/* Header */}
          <div className="p-6 border-b border-slate-100 bg-slate-50/70 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-indigo-600 text-white flex items-center justify-center shadow-xs">
                <Share2 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 text-base">Bagikan Halaman Publik</h3>
                <p className="text-xs text-slate-500">Katalog UIBN Resmi - {typeLabel}</p>
              </div>
            </div>

            <button
              onClick={onClose}
              className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-200/60 transition cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-6 overflow-y-auto min-h-[380px]">
            {/* Entity Badge Card */}
            <div className="p-4 rounded-2xl bg-indigo-50/70 border border-indigo-100 flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <LazyAvatar
                  src={avatarUrl}
                  alt={title}
                  size="md"
                  className="w-11 h-11 rounded-xl bg-white border border-indigo-200 text-indigo-600 shrink-0 shadow-xs"
                  fallbackIcon={
                    type === 'organisasi' ? (
                      <Users className="w-5 h-5 text-amber-500" />
                    ) : type === 'penerbit' ? (
                      <Building2 className="w-5 h-5 text-indigo-600" />
                    ) : (
                      <UserCheck className="w-5 h-5 text-indigo-600" />
                    )
                  }
                />
                <div className="min-w-0">
                  <h4 className="font-bold text-slate-900 text-sm truncate">{title}</h4>
                  <div className="flex items-center gap-2 text-[11px] text-indigo-700">
                    <span className="flex items-center gap-1 font-medium">
                      <ShieldCheck className="w-3.5 h-3.5 text-indigo-600" />
                      <span>{totalBooks} Buku Terdaftar UIBN</span>
                    </span>
                    {publisherCode && (
                      <span className="px-1.5 py-0.2 rounded bg-indigo-100 text-indigo-800 font-mono text-[10px]">
                        Kode: {publisherCode}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Tabs */}
            <div className="flex bg-slate-100 p-1.5 rounded-2xl text-xs font-bold text-slate-500 relative">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`relative flex-1 py-2.5 min-h-[44px] transition-all duration-300 cursor-pointer flex items-center justify-center gap-1.5 z-10 ${
                      isActive ? 'text-indigo-600' : 'hover:text-slate-800'
                    }`}
                  >
                    <Icon className={`w-3.5 h-3.5 transition-transform duration-300 ${isActive ? 'scale-110' : ''}`} />
                    <span className="hidden sm:inline">{tab.label}</span>
                    <span className="sm:hidden">{tab.id === 'link' ? 'Tautan' : tab.id === 'qr' ? 'QR' : 'Widget'}</span>
                    {isActive && (
                      <motion.div
                        layoutId="activeTabPill"
                        className="absolute inset-0 bg-white rounded-xl shadow-sm -z-10"
                        transition={{ type: 'spring', bounce: 0.2, duration: 0.5 }}
                      />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Tab Content with Animation */}
            <div className="relative">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 10, filter: 'blur(4px)' }}
                  animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, y: -10, filter: 'blur(4px)' }}
                  transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                  className="min-h-[280px]"
                >
                  {activeTab === 'link' && (
                    <div className="space-y-4 pt-2">
                      <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
                          Tautan Halaman Publik
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            readOnly
                            value={displayUrl}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono text-slate-700 focus:outline-hidden selection:bg-indigo-100"
                          />
                          <button
                            type="button"
                            onClick={handleCopyLink}
                            className={`h-[42px] px-4 py-2 text-xs font-bold rounded-xl text-white transition-all duration-200 flex items-center gap-1.5 shrink-0 cursor-pointer active:scale-95 ${
                              copiedLink ? 'bg-emerald-600 shadow-emerald-200' : 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-100 shadow-lg'
                            }`}
                          >
                            {copiedLink ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                            <span className="hidden sm:inline">{copiedLink ? 'Tersalin' : 'Salin'}</span>
                          </button>
                        </div>
                      </div>

                      {/* Direct Share Buttons */}
                      <div className="pt-2">
                        <span className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">
                          Bagikan Melalui Platform
                        </span>
                        <div className="grid grid-cols-2 gap-3">
                          <a
                            href={whatsappUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 px-4 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-2xl font-bold text-xs transition-all shadow-md shadow-emerald-100 cursor-pointer active:scale-95"
                          >
                            <MessageCircle className="w-4 h-4" />
                            <span>WhatsApp</span>
                          </a>

                          <a
                            href={displayUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 px-4 py-3 bg-slate-800 hover:bg-slate-900 text-white rounded-2xl font-bold text-xs transition-all shadow-md shadow-slate-100 cursor-pointer active:scale-95"
                          >
                            <ExternalLink className="w-4 h-4" />
                            <span>Kunjungi</span>
                          </a>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'qr' && (
                    <div className="flex flex-col items-center justify-center pt-2 space-y-4">
                      <p className="text-xs text-slate-500 text-center max-w-xs leading-relaxed">
                        Gunakan kamera ponsel Anda untuk memindai kode QR ini dan mengakses profil resmi UIBN.
                      </p>

                      <motion.div 
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="p-5 bg-white border border-slate-100 rounded-3xl shadow-xl shadow-indigo-900/5 relative group"
                      >
                        <QrCodeViewer uibn={shareUrl} directUrl={qrUrl || shareUrl} bookTitle={title} size={180} />
                        <div className="absolute inset-0 bg-indigo-600/5 opacity-0 group-hover:opacity-100 transition-opacity rounded-3xl pointer-events-none" />
                      </motion.div>

                      <div className="text-center space-y-1">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Alamat URL Resmi:</span>
                        <a
                          href={displayUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-indigo-600 hover:text-indigo-800 hover:underline font-mono block break-all px-6 transition"
                        >
                          {displayUrl}
                        </a>
                      </div>
                    </div>
                  )}

                  {activeTab === 'widget' && (
                    <div className="space-y-5 pt-2">
                      <div className="space-y-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">
                            Pilih Skema Warna Widget
                          </label>
                          <div className="flex bg-slate-100 p-1 rounded-xl gap-1">
                            {(['light', 'dark', 'indigo'] as const).map((t) => (
                              <button
                                key={t}
                                onClick={() => setWidgetTheme(t)}
                                className={`px-4 py-1.5 text-[10px] font-black rounded-lg transition-all capitalize cursor-pointer ${
                                  widgetTheme === t 
                                    ? 'bg-white text-indigo-600 shadow-sm' 
                                    : 'text-slate-500 hover:text-slate-800'
                                }`}
                              >
                                {t}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Widget Preview Box */}
                        <div className="border border-slate-100 rounded-3xl p-6 sm:p-8 bg-slate-50/50 flex flex-col items-center justify-center relative group">
                          <div className="absolute inset-0 bg-[radial-gradient(#6366f1_1px,transparent_1px)] [background-size:20px_20px] opacity-10" />
                          <div className="w-full max-w-[320px] shadow-2xl shadow-indigo-950/10 rounded-2xl overflow-hidden transform transition-all duration-500 hover:scale-[1.02] relative z-10">
                            <div 
                              style={{ 
                                background: widgetTheme === 'dark' ? '#1e293b' : widgetTheme === 'indigo' ? '#4f46e5' : '#ffffff',
                                color: widgetTheme === 'light' ? '#1e293b' : '#ffffff',
                                border: `1px solid ${widgetTheme === 'light' ? '#f1f5f9' : widgetTheme === 'indigo' ? '#6366f1' : '#334155'}`,
                              }}
                              className="p-5 rounded-2xl"
                            >
                              <div className="flex items-center gap-3.5 mb-4">
                                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center overflow-hidden border-2 border-indigo-500/10 shrink-0">
                                  <LazyAvatar
                                    src={avatarUrl}
                                    alt={title}
                                    size="lg"
                                    className="w-full h-full object-cover"
                                    fallbackIcon={
                                      type === 'organisasi' ? (
                                        <Users className="w-6 h-6 text-amber-400" />
                                      ) : type === 'penerbit' ? (
                                        <Building2 className="w-6 h-6 text-indigo-300" />
                                      ) : (
                                        <UserCheck className="w-6 h-6 text-indigo-300" />
                                      )
                                    }
                                  />
                                </div>
                                <div className="min-w-0">
                                  <div className="font-black text-[15px] truncate tracking-tight">{title}</div>
                                  <div className="text-[9px] font-black uppercase tracking-widest opacity-60">
                                    {typeLabel} TERVERIFIKASI
                                  </div>
                                </div>
                              </div>
                              <div className="text-xs mb-5 opacity-90 leading-relaxed font-medium">
                                Katalog resmi berisikan <span className="font-bold">{totalBooks} naskah buku</span> ber-UIBN digital yang telah terverifikasi.
                              </div>
                              <div 
                                className={`py-3 rounded-xl text-center text-xs font-black tracking-wide transition-all shadow-md ${
                                  widgetTheme === 'indigo' ? 'bg-white text-indigo-600' : 'bg-indigo-600 text-white'
                                }`}
                              >
                                Lihat Katalog Publik
                              </div>
                            </div>
                          </div>
                          <p className="text-[10px] font-bold text-slate-400 mt-5 text-center flex items-center gap-2">
                            <Sparkles className="w-3 h-3" />
                            <span>Pratinjau Widget Tersemat</span>
                          </p>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">
                              Salin Kode HTML
                            </label>
                            <button
                              onClick={handleCopyWidget}
                              className={`flex items-center gap-1.5 text-[11px] font-black transition-colors ${
                                copiedWidget ? 'text-emerald-600' : 'text-indigo-600 hover:text-indigo-800'
                              }`}
                            >
                              {copiedWidget ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                              <span>{copiedWidget ? 'Kode Tersalin!' : 'Salin Kode Widget'}</span>
                            </button>
                          </div>
                          <div className="relative">
                            <pre className="p-4 bg-slate-900 rounded-2xl text-[10px] font-mono text-slate-300 overflow-x-auto whitespace-pre-wrap max-h-24 scrollbar-thin scrollbar-thumb-slate-700 leading-relaxed border border-slate-800 shadow-inner">
                              {getWidgetCode()}
                            </pre>
                            <div className="absolute top-2 right-2 flex items-center gap-1.5">
                              <span className="w-2 h-2 rounded-full bg-slate-700"></span>
                              <span className="w-2 h-2 rounded-full bg-slate-700"></span>
                              <span className="w-2 h-2 rounded-full bg-slate-700"></span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex justify-end shrink-0">
            <button
              onClick={onClose}
              className="px-5 py-2 text-xs font-semibold rounded-xl bg-slate-200 hover:bg-slate-300 text-slate-700 transition cursor-pointer"
            >
              Tutup
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
};
